import { Calendar, Clock, Users, Info } from 'lucide-react';
import { Button } from '../ui/Button';
import { formatRelativeTime, type NotificationEvent } from '../../lib/notifications';

interface NotificationDetailPanelProps {
  notification: NotificationEvent | null;
  isRead: boolean;
  onViewReservation: (notification: NotificationEvent) => void;
}

const NotificationDetailPanel: React.FC<NotificationDetailPanelProps> = ({
  notification,
  isRead,
  onViewReservation,
}) => {
  const getSeverityColor = (type: NotificationEvent['type']) => {
    switch (type) {
      case 'NEW_RESERVATION_ONLINE':
        return 'text-blue-700 bg-blue-50 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-900';
      case 'NEW_RESERVATION_MANUAL':
        return 'text-indigo-700 bg-indigo-50 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-900';
      case 'CANCELLATION':
        return 'text-red-700 bg-red-50 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-900';
      case 'MODIFICATION':
        return 'text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-900';
      case 'CUSTOMER_ARRIVED':
        return 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-900';
      case 'TABLE_FREED':
        return 'text-violet-700 bg-violet-50 border-violet-200 dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-900';
      default:
        return 'text-slate-700 bg-slate-50 border-slate-200 dark:bg-slate-900/30 dark:text-slate-400 dark:border-slate-800';
    }
  };

  const getHeaderBorder = (type: NotificationEvent['type']) => {
    switch (type) {
      case 'NEW_RESERVATION_ONLINE':
        return 'border-t-4 border-t-blue-500';
      case 'NEW_RESERVATION_MANUAL':
        return 'border-t-4 border-t-indigo-500';
      case 'CANCELLATION':
        return 'border-t-4 border-t-red-500';
      case 'MODIFICATION':
        return 'border-t-4 border-t-amber-500';
      case 'CUSTOMER_ARRIVED':
        return 'border-t-4 border-t-emerald-500';
      case 'TABLE_FREED':
        return 'border-t-4 border-t-violet-500';
      default:
        return 'border-t-4 border-t-slate-500';
    }
  };

  if (!notification) {
    return (
      <aside className="rounded-lg border border-border bg-card p-6 h-fit mt-7 shadow-sm">
        <div className="flex flex-col items-center justify-center text-center space-y-3 py-8">
          <div className="p-3 bg-muted rounded-full">
            <Info className="w-6 h-6 text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground max-w-[200px]">
            Επίλεξε μια ειδοποίηση για να δεις πλήρεις λεπτομέρειες.
          </p>
        </div>
      </aside>
    );
  }

  return (
    <aside className={`rounded-lg border border-border bg-card p-6 h-fit mt-7 space-y-6 shadow-sm ${getHeaderBorder(notification.type)}`}>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-xl font-semibold text-foreground leading-tight">{notification.title}</h2>
          {!isRead && (
            <span className="shrink-0 inline-flex items-center rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-medium border border-primary/20">
              Νέο
            </span>
          )}
        </div>

        <div className={`p-3 rounded-md border text-sm font-medium ${getSeverityColor(notification.type)}`}>
          {notification.description}
        </div>
      </div>

      <div className="space-y-3 text-sm pt-2 border-t border-border/50">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="w-4 h-4 text-foreground/50" />
          <span className="font-medium text-foreground">{formatRelativeTime(notification.timestamp)}</span>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Calendar className="w-4 h-4" />
          <span>
            {notification.reservationDate} • {notification.reservationTime}
          </span>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Users className="w-4 h-4" />
          <span>
            {notification.customerName} • {notification.partySize} άτομα
          </span>
        </div>
      </div>

      <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground space-y-1">
        <div>Τραπέζι: {notification.tableLabel}</div>
        <div>
          Πηγή: {notification.source === 'online' ? 'Online' : notification.source === 'manual' ? 'Χειροκίνητα' : notification.source}
        </div>
        {notification.notes && (
          <div className="pt-1 mt-1 border-t border-border/60 text-xs leading-snug">
            <span className="font-medium text-foreground">Σημειώσεις:</span>{' '}
            <span className="break-words">{notification.notes}</span>
          </div>
        )}
      </div>

      <Button
        onClick={() => onViewReservation(notification)}
        className="w-full"
      >
        Προβολή κρατήσεων
      </Button>
    </aside>
  );
};

export default NotificationDetailPanel;
